import { useEffect } from 'react';
import pingCoachPresence from 'actions/presenceApi';
/**
 * Visibility-gated coach presence ping (SCO-469, escalation ADR §4 amendment
 * item 5). While the coach surface is actually ON SCREEN, refresh the
 * backend's `presence:coach:{id}` key so morning-checkin pushes are skipped
 * for a coach already looking at the app. A hidden/backgrounded tab must NOT
 * ping — an open socket does not mean anyone is looking, and every overcount
 * is a push that should have fired and didn't. A closed or hidden client just
 * stops pinging and the key ages out (60s TTL, self-healing).
 */
/**
 * Initial cadence: half the server's default 60s TTL, so presence never
 * flickers between pings. The first response's echoed TTL takes over.
 */
export var PRESENCE_PING_INTERVAL_MS = 30 * 1000;
/**
 * Start the ping loop: ping immediately if visible, re-ping on the interval,
 * pause while hidden, resume instantly when the surface becomes visible
 * again. Returns a stop function tearing down the listener + interval.
 * Exported separately from the hook so the scheduling is unit-testable
 * without a React renderer.
 */
export var startCoachPresencePing = function (ping) {
    if (ping === void 0) { ping = pingCoachPresence; }
    var intervalId = null;
    var intervalMs = PRESENCE_PING_INTERVAL_MS;
    var controller = null;
    var inFlight = false;
    var stopped = false;
    /*
     * One ping at a time, hard-cancelled on hide/stop (Codex round 1, #408
     * P1): a request started while visible must not land after the coach
     * looks away and assert presence for another TTL — that is the prohibited
     * overcounting direction. The echoed TTL drives the cadence (TTL/2), as
     * the API contract promises.
     */
    var firePing = function () {
        if (inFlight) {
            return; // serialized: never stack requests behind a slow one
        }
        inFlight = true;
        controller = new AbortController();
        ping(controller.signal)
            .then(function (ttlSeconds) {
            if (stopped || ttlSeconds === null) {
                return;
            }
            var nextMs = (ttlSeconds / 2) * 1000;
            if (nextMs !== intervalMs) {
                intervalMs = nextMs;
                if (intervalId !== null) {
                    clearInterval(intervalId);
                    intervalId = setInterval(firePing, intervalMs);
                }
            }
        })
            .catch(function () { return undefined; })
            .finally(function () {
            inFlight = false;
            controller = null;
        });
    };
    var stopLoop = function () {
        if (intervalId !== null) {
            clearInterval(intervalId);
            intervalId = null;
        }
        controller === null || controller === void 0 ? void 0 : controller.abort();
    };
    var startLoop = function () {
        if (intervalId !== null) {
            return;
        }
        firePing();
        intervalId = setInterval(firePing, intervalMs);
    };
    var onVisibilityChange = function () {
        if (document.visibilityState === 'visible') {
            startLoop();
        }
        else {
            stopLoop();
        }
    };
    // pagehide is the bfcache-compatible teardown signal (Codex round 2):
    // navigations and tab kills don't reliably fire visibilitychange, and a
    // bfcache-frozen page must not leave an interval or request behind. Mobile
    // process kills can skip both events — the 60s TTL bounds that tail.
    var onPageHide = function () { return stopLoop(); };
    // pageshow is pagehide's mirror (Codex round 3, #408 P2): a bfcache
    // restore (back/forward navigation) revives the page WITHOUT a mount and
    // may not fire visibilitychange — without this, a restored coach surface
    // looks present to the user but never pings again. Same visibility gate:
    // a restore into a hidden tab stays silent.
    var onPageShow = function () { return onVisibilityChange(); };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    onVisibilityChange();
    return function () {
        stopped = true;
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('pagehide', onPageHide);
        window.removeEventListener('pageshow', onPageShow);
        stopLoop();
    };
};
/**
 * Mount the presence ping for the current user. Pass `enabled: false` for
 * producers (salespersons) — presence gates COACH-facing pushes only, and the
 * producer surfaces must not spend requests on it.
 */
var useCoachPresencePing = function (enabled) {
    useEffect(function () {
        if (!enabled) {
            return undefined;
        }
        return startCoachPresencePing();
    }, [enabled]);
};
export default useCoachPresencePing;
